import { useEffect, useState } from "react";
import { classNames } from "primereact/utils";
import { AppDialog } from "../../common/components/AppDialog/AppDialog";
import { PaymentTypes } from "../../common/enums";
import { IGymUserSearchResult, getMemberFullName } from "./models/gymModels";
import styles from "./GymPaymentModal.module.scss";

interface IGymPaymentOption {
  id: PaymentTypes;
  name: string;
}

interface IGymPaymentModalProps {
  visible: boolean;
  member: IGymUserSearchResult | null;
  defaultPrice: number;
  onConfirm: (paymentType: PaymentTypes, price: number) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  paymentOptions: IGymPaymentOption[];
  /** Percent of the entered price the studio keeps after Multisport taxes (e.g. 80). */
  multisportPercentLeft?: number | null;
  /** Percent of the entered price the studio keeps after CoolFit taxes (e.g. 80). */
  coolFitPercentLeft?: number | null;
}

const DEFAULT_GYM_PRICE = 15;

export const GymPaymentModal = ({
  visible,
  member,
  defaultPrice,
  onConfirm,
  onCancel,
  isSubmitting,
  paymentOptions,
  multisportPercentLeft,
  coolFitPercentLeft,
}: IGymPaymentModalProps) => {
  const [selected, setSelected] = useState<PaymentTypes | null>(null);
  const [priceText, setPriceText] = useState(String(defaultPrice || DEFAULT_GYM_PRICE));

  useEffect(() => {
    if (visible) {
      setSelected(null);
      setPriceText(String(defaultPrice || DEFAULT_GYM_PRICE));
    }
  }, [visible, defaultPrice]);

  const handleConfirm = () => {
    if (!selected) return;
    const price = parseFloat(priceText.replace(",", "."));
    if (isNaN(price) || price < 0) return;
    onConfirm(selected, price);
  };

  // Multisport / CoolFit keep a partner commission, so the BE stores only the studio's
  // profit share. Preview that amount here so staff see the real price before confirming.
  const activePercentLeft =
    selected === PaymentTypes.Multisport
      ? multisportPercentLeft
      : selected === PaymentTypes.CoolFit
        ? coolFitPercentLeft
        : null;
  const showPartnerDisclaimer = activePercentLeft != null && activePercentLeft > 0;
  const parsedPrice = parseFloat(priceText.replace(",", "."));
  // Mirrors decimal.Round(price * pct / 100, 2, MidpointRounding.ToZero) on the server.
  const finalPartnerPrice =
    showPartnerDisclaimer && Number.isFinite(parsedPrice) && parsedPrice >= 0
      ? Math.trunc(parsedPrice * activePercentLeft!) / 100
      : null;

  return (
    <AppDialog
      header={
        member
          ? `Заплащане на влизане — ${getMemberFullName(member)}`
          : "Заплащане на влизане"
      }
      visible={visible}
      onHide={onCancel}
      className={styles.dialog}
      style={{ width: "440px", maxWidth: "92vw" }}
    >
      <div className={styles.content}>
        <div className={styles.methods}>
          {paymentOptions.map((option) => (
            <div
              key={option.id}
              className={classNames(styles.method, {
                [styles.method_selected]: selected === option.id,
              })}
              onClick={() => setSelected(option.id)}
            >
              <div className={styles.method_name}>{option.name}</div>
              <div className={styles.radio}>
                <div className={styles.radio_dot} />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.priceField}>
          <label className={styles.priceLabel}>Цена</label>
          <div className={styles.priceInputWrapper}>
            <input
              type="text"
              inputMode="decimal"
              className={styles.priceInput}
              value={priceText}
              onChange={(e) => setPriceText(e.target.value)}
              placeholder="0.00"
            />
            <span className={styles.priceCurrency}>€</span>
          </div>
        </div>

        {showPartnerDisclaimer && (
          <div className={styles.disclaimer}>
            <span>
              Внимание: крайната цена ще бъде изчислена като{" "}
              <strong>{activePercentLeft}%</strong> от въведената сума
              {finalPartnerPrice !== null && (
                <>
                  {" "}
                  — <strong>{finalPartnerPrice.toFixed(2)} €</strong>
                </>
              )}
              .
            </span>
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel_btn}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Отказ
          </button>
          <button
            type="button"
            className={styles.confirm_btn}
            disabled={!selected || isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting ? "Обработва се..." : "Потвърди влизане"}
          </button>
        </div>
      </div>
    </AppDialog>
  );
};
